import React from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "../../utils/cn";

const Checkbox = React.forwardRef(({
    className,
    id,
    checked,
    indeterminate = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    size = "default",
    onChange,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const checkboxId = id || `checkbox-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Size variants
    const sizeClasses = {
        sm: "h-4 w-4",
        default: "h-5 w-5",
        lg: "h-6 w-6"
    };

    const handleChange = (e) => {
        if (onChange) {
            onChange(e?.target?.checked, e);
        }
    };

    return (
        <div className={cn("flex items-start space-x-3", className)}>
            <div className="relative flex items-center">
                <input
                    type="checkbox"
                    ref={ref}
                    id={checkboxId}
                    checked={checked}
                    disabled={disabled}
                    required={required}
                    onChange={handleChange}
                    className="sr-only peer"
                    {...props}
                />

                <label
                    htmlFor={checkboxId}
                    className={cn(
                        "relative cursor-pointer rounded border-2 transition-all duration-200 ease-in-out flex items-center justify-center",
                        sizeClasses?.[size],
                        checked 
                            ? "bg-blue-600 border-blue-600 text-white" :"bg-white border-gray-300 hover:border-blue-400",
                        indeterminate && "bg-blue-600 border-blue-600 text-white",
                        error && !checked && "border-red-300 bg-red-50",
                        disabled && "cursor-not-allowed opacity-50"
                    )}
                >
                    {checked && !indeterminate && (
                        <Check className={cn(
                            "text-white",
                            size === "sm" ? "w-3 h-3" : 
                            size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5"
                        )} />
                    )}
                    {indeterminate && (
                        <Minus className={cn(
                            "text-white",
                            size === "sm" ? "w-3 h-3" : 
                            size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5"
                        )} />
                    )}
                </label>
            </div>
            
            {(label || description || error) && (
                <div className="flex-1 space-y-1">
                    {label && (
                        <label
                            htmlFor={checkboxId}
                            className={cn(
                                "text-sm leading-relaxed cursor-pointer select-none",
                                error ? "text-red-600" : "text-gray-700",
                                disabled && "cursor-not-allowed opacity-50"
                            )}
                        >
                            {label}
                            {required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                    )}

                    {description && !error && (
                        <p className="text-sm text-gray-500">
                            {description}
                        </p>
                    )}

                    {error && (
                        <p className="text-sm text-red-600">
                            {error}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
});

Checkbox.displayName = "Checkbox";

// Checkbox Group component
const CheckboxGroup = React.forwardRef(({
    className,
    children,
    label,
    description,
    error,
    required = false,
    disabled = false,
    ...props
}, ref) => {
    return (
        <fieldset
            ref={ref}
            disabled={disabled}
            className={cn("space-y-3", className)}
            {...props}
        >
            {label && (
                <legend className={cn(
                    "text-sm font-medium",
                    error ? "text-red-600" : "text-gray-900"
                )}>
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </legend>
            )}

            {description && !error && (
                <p className="text-sm text-gray-600">
                    {description}
                </p>
            )}

            <div className="space-y-3">
                {children}
            </div>

            {error && (
                <p className="text-sm text-red-600">
                    {error}
                </p>
            )}
        </fieldset>
    );
});

CheckboxGroup.displayName = "CheckboxGroup";

export { Checkbox, CheckboxGroup };